import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "block text-sm font-medium text-gray-700" }
const _hoisted_2 = {
  key: 0,
  class: "text-red-500"
}
const _hoisted_3 = { class: "py-2" }
const _hoisted_4 = { class: "relative" }
const _hoisted_5 = { class: "relative mb-6" }
const _hoisted_6 = { class: "w-full h-2 bg-gray-200 rounded-lg relative" }
const _hoisted_7 = { class: "flex justify-between text-sm text-gray-600" }
const _hoisted_8 = { class: "text-blue-600 font-medium" }
const _hoisted_9 = { class: "text-blue-600 font-medium" }
const _hoisted_10 = { class: "p-3 bg-blue-50 rounded-lg" }
const _hoisted_11 = { class: "text-sm text-blue-800 font-medium" }
const _hoisted_12 = { class: "text-blue-600" }

import { ref, watch, onUnmounted } from 'vue';
import type { PriceRangeFieldProps } from '@/types/sellerRegister';


export default /*@__PURE__*/_defineComponent({
  __name: 'PriceRangeField',
  props: {
    label: {},
    required: { type: Boolean, default: false },
    modelValue: {},
    min: { default: 0 },
    max: { default: 200 },
    step: { default: 1 },
    gridClass: { default: '' }
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const priceMin = ref(props.modelValue.min);
const priceMax = ref(props.modelValue.max);

// État du drag
const isDragging = ref(false);
const dragType = ref<'min' | 'max' | null>(null);

// Calculer le pourcentage pour le positionnement
const getPercentage = (value: number) => {
  return ((value - props.min) / (props.max - props.min)) * 100;
};

// Calculer la valeur à partir du pourcentage
const getValueFromPercentage = (percentage: number) => {
  const value = props.min + (percentage / 100) * (props.max - props.min);
  return Math.round(value / props.step) * props.step;
};

// Démarrer le drag
const startDragging = (type: 'min' | 'max') => {
  isDragging.value = true;
  dragType.value = type;
  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('mouseup', stopDragging);
};

// Gérer le mouvement de la souris
const handleMouseMove = (event: MouseEvent) => {
  if (!isDragging.value || !dragType.value) return;
  
  const container = document.querySelector('.relative.mb-6');
  if (!container) return;
  
  const containerRect = container.getBoundingClientRect();
  const x = event.clientX - containerRect.left;
  const percentage = Math.max(0, Math.min(100, (x / containerRect.width) * 100));
  const newValue = getValueFromPercentage(percentage);
  
  if (dragType.value === 'min') {
    if (newValue < priceMax.value) {
      priceMin.value = newValue;
    }
  } else if (dragType.value === 'max') {
    if (newValue > priceMin.value) {
      priceMax.value = newValue;
    }
  }
  
  emitChange();
};

// Arrêter le drag
const stopDragging = () => {
  isDragging.value = false;
  dragType.value = null;
  document.removeEventListener('mousemove', handleMouseMove);
  document.removeEventListener('mouseup', stopDragging);
};

// Nettoyer les event listeners
onUnmounted(() => {
  document.removeEventListener('mousemove', handleMouseMove);
  document.removeEventListener('mouseup', stopDragging);
});

// Synchroniser avec le modèle
watch(() => props.modelValue, (newValue) => {
  priceMin.value = newValue.min;
  priceMax.value = newValue.max;
}, { immediate: true });

// Émettre les changements
const emitChange = () => {
  emit('update:modelValue', {
    min: priceMin.value,
    max: priceMax.value
  });
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(_ctx.gridClass)
  }, [
    _createElementVNode("label", _hoisted_1, [
      _createTextVNode(_toDisplayString(_ctx.label) + " ", 1),
      (_ctx.required)
        ? (_openBlock(), _createElementBlock("span", _hoisted_2, "*"))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("div", {
              class: "absolute h-full bg-blue-500 rounded-lg transition-all duration-300",
              style: _normalizeStyle({
                left: `${getPercentage(priceMin.value)}%`,
                width: `${getPercentage(priceMax.value) - getPercentage(priceMin.value)}%`
              })
            }, null, 4)
          ]),
          _createElementVNode("div", {
            class: "absolute top-0 w-6 h-6 bg-blue-600 rounded-full border-2 border-white shadow-lg transform -translate-y-2 cursor-pointer z-20",
            style: _normalizeStyle({ left: `calc(${getPercentage(priceMin.value)}% - 12px)` }),
            onMousedown: _cache[0] || (_cache[0] = ($event: any) => (startDragging('min')))
          }, null, 36),
          _createElementVNode("div", {
            class: "absolute top-0 w-6 h-6 bg-blue-600 rounded-full border-2 border-white shadow-lg transform -translate-y-2 cursor-pointer z-20",
            style: _normalizeStyle({ left: `calc(${getPercentage(priceMax.value)}% - 12px)` }),
            onMousedown: _cache[1] || (_cache[1] = ($event: any) => (startDragging('max')))
          }, null, 36)
        ]),
        _createElementVNode("div", _hoisted_7, [
          _createElementVNode("span", _hoisted_8, _toDisplayString(priceMin.value) + "€", 1),
          _createElementVNode("span", _hoisted_9, _toDisplayString(priceMax.value) + "€", 1)
        ]),
        _createElementVNode("div", _hoisted_10, [
          _createElementVNode("p", _hoisted_11, [
            _cache[2] || (_cache[2] = _createTextVNode(" Gamme de prix sélectionnée : ")),
            _createElementVNode("span", _hoisted_12, _toDisplayString(priceMin.value) + "€ - " + _toDisplayString(priceMax.value) + "€", 1)
          ])
        ])
      ])
    ])
  ], 2))
}
}

})